
import Vue, { PropType } from 'vue';
import { get, isFunction } from 'lodash';
import { Unsubscriber } from '@/core/event-bus';
import Slider from '@/common/slider/Slider.vue';
import Api from '@/core/api';
import { PlayerState } from '@/core/player-state';

interface SeekBarConfig {
    startPosition: number;
}

export default Vue.extend({
    components: {
        Slider,
    },

    props: {
        api: {
            type: Object as PropType<Api>,
            required: true,
        },
        labels: {
            type: Object as PropType<Record<string, unknown>>,
            default: () => ({})
        },
        config: {
            type: Object as PropType<SeekBarConfig>,
            default: () => ({} as SeekBarConfig)
        }
    },

    data() {
        return {
            videoLoaded: false,
            videoDurationSecs: NaN,
            videoElapsedSecs: 0,
            isBeingClicked: false,
            isPlaying: false,
            wasPlayingOnMouseDown: false,
            visible: false,
            unsubscribers: [] as Unsubscriber[]
        };
    },

    computed: {
        startPosition(): number {
            return this.config?.startPosition ?? 0;
        }
    },

    mounted(): void {
        const { unsubscriber, state } = this.api.onStateChange(
            (partial: Partial<PlayerState>) => {
                this.updateState(partial);
            }
        );

        this.updateState(state);
        this.unsubscribers.push(unsubscriber);
    },

    beforeDestroy(): void {
        for (const unsubscriber of this.unsubscribers) {
            if (isFunction(unsubscriber)) {
                unsubscriber();
            }
        }
    },

    methods: {
        onMouseDown(): void {
            this.isBeingClicked = true;
            this.wasPlayingOnMouseDown = this.isPlaying;
            this.api.pause();
        },

        onMouseUp(): void {
            this.isBeingClicked = false;
            if (this.wasPlayingOnMouseDown) {
                this.api.play();
            }
        },

        onProgressSliderInput(manualElapsedSeconds: number): void {
            this.videoElapsedSecs = manualElapsedSeconds;
            this.api.seek(manualElapsedSeconds);
        },

        updateState(state: Partial<PlayerState>) {
            this.videoLoaded = get(state, 'videoLoaded', this.videoLoaded);
            this.videoDurationSecs = Math.round(get(state, 'videoDurationSecs', this.videoDurationSecs));
            this.visible = get(state, 'isControlBarVisible', this.visible);
            this.isPlaying = get(state, 'isPlaying', this.isPlaying);

            if (!this.isBeingClicked) {
                // Only update when the elapsed seconds actually change. Otherwise this is prone to race conditions
                // in which `this.videoElapsedSecs` is incorrectly updated between a `seek` request and when the
                // event corresponding to that request is received.
                this.videoElapsedSecs = Math.round(get(state, 'videoElapsedSecs', this.videoElapsedSecs));
            }
        }
    }
});
